import { useState, useEffect, FormEvent } from 'react';
import {
  TextField, Button, Typography, Alert, CircularProgress,
  Box, InputAdornment, IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { AuthLayout } from '@/components/AuthLayout';
import { getPasswordResetTokenDirect, resetPasswordDirect } from '@timetable/api';
import { validatePassword, getPasswordRequirementsText } from '@timetable/utils';

type PageState = 'loading' | 'form' | 'done' | 'invalid';

export function ResetPasswordPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token') ?? '';

  const [pageState, setPageState] = useState<PageState>('loading');
  const [email, setEmail] = useState('');
  const [invalidReason, setInvalidReason] = useState('');

  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [passwordError, setPasswordError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState('');

  useEffect(() => {
    if (!token) {
      setInvalidReason('No reset token found in the link.');
      setPageState('invalid');
      return;
    }

    getPasswordResetTokenDirect(token).then((result) => {
      if (!result.valid) {
        setInvalidReason(result.error ?? 'Invalid reset link.');
        setPageState('invalid');
      } else {
        setEmail(result.email);
        setPageState('form');
      }
    });
  }, [token]);

  const validate = () => {
    const result = validatePassword(password, confirmPassword);
    if (!result.valid) {
      setPasswordError(result.errors.password || result.errors.confirmPassword || '');
      return false;
    }
    setPasswordError('');
    return true;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    
    setSubmitting(true);
    setSubmitError('');
    
    try {
      await resetPasswordDirect(token, password);
      setPageState('done');
    } catch (err: any) {
      setSubmitError(err.message ?? 'Password reset failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (pageState === 'loading') {
    return (
      <AuthLayout>
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
          <CircularProgress />
        </Box>
      </AuthLayout>
    );
  }

  if (pageState === 'invalid') {
    return (
      <AuthLayout>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Invalid Reset Link</Typography>
        <Alert severity="error" sx={{ mb: 2 }}>{invalidReason}</Alert>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          The reset link may have expired or already been used. Please request a new one.
        </Typography>
        <Button variant="outlined" fullWidth onClick={() => navigate('/forgot-password')}>
          Request New Reset Link
        </Button>
        <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          <Button variant="text" onClick={() => navigate('/login')}>
            Back to Login
          </Button>
        </Typography>
      </AuthLayout>
    );
  }

  if (pageState === 'done') {
    return (
      <AuthLayout>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 1 }}>Password Reset Complete!</Typography>
        <Alert severity="success" sx={{ mb: 2 }}>
          Your password has been updated successfully. You can now sign in with your new password.
        </Alert>
        <Button variant="contained" fullWidth onClick={() => navigate('/login')}>
          Go to Login
        </Button>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <Typography variant="h4" sx={{ fontWeight: 600, mb: 0.5 }}>Reset Your Password</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Enter a new password for <strong>{email}</strong>
      </Typography>

      {submitError && <Alert severity="error" sx={{ mb: 2 }}>{submitError}</Alert>}

      <form onSubmit={handleSubmit}>
        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>New Password</Typography>
        <TextField
          fullWidth
          size="small"
          type={showPassword ? 'text' : 'password'}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (passwordError) setPasswordError('');
          }}
          required
          placeholder="••••••••"
          helperText={getPasswordRequirementsText()}
          autoFocus
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton size="small" onClick={() => setShowPassword((s) => !s)}>
                  {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                </IconButton>
              </InputAdornment>
            ),
          }}
          sx={{ mb: 2 }}
        />

        <Typography variant="body2" sx={{ mb: 0.5, fontWeight: 500 }}>Confirm New Password</Typography>
        <TextField
          fullWidth
          size="small"
          type={showPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (passwordError) setPasswordError('');
          }}
          required
          placeholder="••••••••"
          error={!!passwordError}
          helperText={passwordError}
          sx={{ mb: 3 }}
        />

        <Button fullWidth type="submit" variant="contained" size="large" disabled={submitting}>
          {submitting ? <CircularProgress size={22} color="inherit" /> : 'Update Password'}
        </Button>
      </form>
    </AuthLayout>
  );
}